'use client';

import React from 'react';
import { XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Asset } from './AssetItem';

interface AssetPreviewModalProps {
  asset: Asset | null;
  mediaUrl: string | null;
  isOpen: boolean;
  onClose: () => void;
  onModify?: () => void;
}

export default function AssetPreviewModal({
  asset,
  mediaUrl,
  isOpen,
  onClose,
  onModify
}: AssetPreviewModalProps) {
  if (!isOpen || !asset) return null;

  const isImage = asset.contentType === 'image';
  const isVideo = asset.contentType === 'video';
  const canModify = isImage && asset.sourceType === 'file'; // Only allow modification for file-based images

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative max-w-7xl w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4 px-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white truncate">{asset.name}</h2>
            {asset.description && (
              <p className="text-sm text-gray-300 mt-1">{asset.description}</p>
            )}
          </div>
          <div className="flex items-center space-x-2 ml-4">
            {canModify && onModify && (
              <button
                onClick={onModify}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Modify
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              aria-label="Close preview"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center overflow-hidden">
          {isImage && mediaUrl ? (
            <img
              key={mediaUrl}
              src={mediaUrl}
              alt={asset.name}
              className="max-w-full max-h-full object-contain"
            />
          ) : isVideo && mediaUrl ? (
            <video
              key={mediaUrl}
              src={mediaUrl}
              controls
              autoPlay
              className="max-w-full max-h-full"
            />
          ) : (
            <div className="text-white text-center">
              <p className="text-lg">Preview not available for this asset type</p>
              <p className="text-sm text-gray-400 mt-2">Type: {asset.contentType}</p>
            </div>
          )}
        </div>

        {/* Footer with metadata */}
        <div className="mt-4 px-4 py-3 bg-black bg-opacity-50 rounded-lg">
          <div className="flex items-center justify-between text-sm text-gray-300">
            <div className="flex items-center space-x-4">
              <span className="capitalize">{asset.contentType}</span>
              <span>•</span>
              <span>{asset.size}</span>
              <span>•</span>
              <span>Uploaded {asset.uploadDate}</span>
            </div>
            {asset.tags && asset.tags.length > 0 && (
              <div className="flex items-center space-x-2">
                {asset.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-1 bg-blue-600 bg-opacity-50 text-white rounded-full text-xs"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}